import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Wrench, Cog, Palette, Euro, MapPin, PlusCircle, Package, Users, BellRing } from 'lucide-react';
import { getListings, getMachineManufacturers, getVerticalCategories } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { SaveSearchDialog } from '@/components/listings/SaveSearchDialog';

const VERTICAL_META = {
  spare_parts: { label: 'Ersatzteile & Module', icon: Wrench, color: 'text-orange-500' },
  machines: { label: 'Druckmaschinen', icon: Cog, color: 'text-blue-500' },
  paints: { label: 'Farben, Lacke & Additive', icon: Palette, color: 'text-fuchsia-500' },
};

export default function ListingsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const vertical = params.get('vertical') || 'spare_parts';

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: '', category_node_id: '', manufacturer_id: '', condition: '',
    min_price: '', max_price: '', country: '', year_min: '', year_max: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await getListings({ vertical, page, limit: 20, ...cleanFilters });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [vertical, page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, mfrs] = await Promise.all([
          getVerticalCategories(vertical),
          vertical === 'machines' || vertical === 'spare_parts' || vertical === 'paints'
            ? getMachineManufacturers({ active_only: true }) : Promise.resolve({ data: [] }),
        ]);
        setCategories(cats.data || []);
        setManufacturers(mfrs.data || []);
      } catch { /* ignore */ }
    })();
    setPage(1);
    // Initialise filters from URL (supports saved-search "Öffnen" deep-links)
    const fromUrl = (key) => params.get(key) || '';
    setFilters({
      search: fromUrl('search'),
      category_node_id: fromUrl('category_node_id'),
      manufacturer_id: fromUrl('manufacturer_id'),
      condition: fromUrl('condition'),
      min_price: fromUrl('min_price'),
      max_price: fromUrl('max_price'),
      country: fromUrl('country'),
      year_min: fromUrl('year_min'),
      year_max: fromUrl('year_max'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical]);

  const meta = VERTICAL_META[vertical] || VERTICAL_META.spare_parts;
  const IconEl = meta.icon;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8" data-testid={`listings-page-${vertical}`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <IconEl className={`w-8 h-8 ${meta.color}`} />
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="listings-page-heading">
                {meta.label}
              </h1>
              <p className="text-sm text-muted-foreground">{total} aktive Einträge</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={vertical === 'spare_parts' ? 'default' : 'outline'}
              onClick={() => setParams({ vertical: 'spare_parts' })}
              data-testid="switch-vertical-spare-parts"
            >
              <Wrench className="w-4 h-4 mr-2" /> Ersatzteile
            </Button>
            <Button
              variant={vertical === 'machines' ? 'default' : 'outline'}
              onClick={() => setParams({ vertical: 'machines' })}
              data-testid="switch-vertical-machines"
            >
              <Cog className="w-4 h-4 mr-2" /> Maschinen
            </Button>
            <Button
              variant={vertical === 'paints' ? 'default' : 'outline'}
              onClick={() => setParams({ vertical: 'paints' })}
              data-testid="switch-vertical-paints"
            >
              <Palette className="w-4 h-4 mr-2" /> Farben & Lacke
            </Button>
            {user && (
              <Button onClick={() => navigate(`/listing/new?vertical=${vertical}`)} data-testid="create-listing-btn">
                <PlusCircle className="w-4 h-4 mr-2" /> Inserat erstellen
              </Button>
            )}
            {user && (
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(true)}
                data-testid="save-search-btn"
              >
                <BellRing className="w-4 h-4 mr-2" /> Suche speichern
              </Button>
            )}
          </div>
        </div>

        <SaveSearchDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          vertical={vertical}
          criteria={filters}
        />

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* Filters */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-base">Filter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="search">Suche</Label>
                <Input
                  id="search"
                  placeholder="Titel, Hersteller, Modell…"
                  value={filters.search}
                  onChange={e => setFilters({ ...filters, search: e.target.value })}
                  data-testid="listings-filter-search"
                />
              </div>
              <div>
                <Label>Kategorie</Label>
                <Select value={filters.category_node_id || 'all'} onValueChange={v => setFilters({ ...filters, category_node_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="listings-filter-category"><SelectValue placeholder="Alle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.node_id} value={c.node_id}>
                        {c.depth > 1 ? '– ' : ''}{c.node_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hersteller</Label>
                <Select value={filters.manufacturer_id || 'all'} onValueChange={v => setFilters({ ...filters, manufacturer_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="listings-filter-manufacturer"><SelectValue placeholder="Alle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Hersteller</SelectItem>
                    {manufacturers.map(m => (
                      <SelectItem key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zustand</Label>
                <Select value={filters.condition || 'all'} onValueChange={v => setFilters({ ...filters, condition: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="listings-filter-condition"><SelectValue placeholder="Alle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="new">Neu</SelectItem>
                    <SelectItem value="refurbished">Generalüberholt</SelectItem>
                    <SelectItem value="used_excellent">Gebraucht – sehr gut</SelectItem>
                    <SelectItem value="used_good">Gebraucht – gut</SelectItem>
                    <SelectItem value="used_fair">Gebraucht – akzeptabel</SelectItem>
                    <SelectItem value="for_parts">Für Ersatzteile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Min € </Label>
                  <Input type="number" value={filters.min_price} onChange={e => setFilters({ ...filters, min_price: e.target.value })} data-testid="listings-filter-min-price" />
                </div>
                <div>
                  <Label>Max €</Label>
                  <Input type="number" value={filters.max_price} onChange={e => setFilters({ ...filters, max_price: e.target.value })} data-testid="listings-filter-max-price" />
                </div>
              </div>
              {vertical === 'machines' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Baujahr ab</Label>
                    <Input type="number" value={filters.year_min} onChange={e => setFilters({ ...filters, year_min: e.target.value })} data-testid="listings-filter-year-min" />
                  </div>
                  <div>
                    <Label>Baujahr bis</Label>
                    <Input type="number" value={filters.year_max} onChange={e => setFilters({ ...filters, year_max: e.target.value })} data-testid="listings-filter-year-max" />
                  </div>
                </div>
              )}
              <div>
                <Label>Land</Label>
                <Input placeholder="z.B. DE" value={filters.country} onChange={e => setFilters({ ...filters, country: e.target.value.toUpperCase() })} data-testid="listings-filter-country" />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setFilters({ search: '', category_node_id: '', manufacturer_id: '', condition: '', min_price: '', max_price: '', country: '', year_min: '', year_max: '' })}
                data-testid="listings-filter-reset"
              >
                Filter zurücksetzen
              </Button>
            </CardContent>
          </Card>

          {/* Grid */}
          <div>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56" />)}
              </div>
            ) : items.length === 0 ? (
              <Card>
                <CardContent className="py-16 flex flex-col items-center text-center text-muted-foreground">
                  <Package className="w-12 h-12 mb-3 opacity-40" />
                  <p>Noch keine aktiven {meta.label}-Inserate.</p>
                  {user && (
                    <Button className="mt-4" onClick={() => navigate(`/listing/new?vertical=${vertical}`)} data-testid="listings-empty-create-btn">
                      Erstes Inserat erstellen
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
                {items.map(l => <ListingCard key={l.listing_id} listing={l} />)}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function ListingCard({ listing }) {
  const conditionLabel = {
    new: 'Neu', refurbished: 'Generalüberholt',
    used_excellent: 'Gebraucht – sehr gut', used_good: 'Gebraucht – gut',
    used_fair: 'Akzeptabel', for_parts: 'Für Teile',
  }[listing.condition] || '—';
  const photo = (listing.photos || [])[0];
  return (
    <Link to={`/listing/${listing.listing_id}`} className="block" data-testid={`listing-card-${listing.listing_id}`}>
      <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden">
        <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
          {photo ? (
            <img src={photo} alt={listing.title} className="w-full h-full object-cover" />
          ) : (
            <Package className="w-12 h-12 text-muted-foreground/40" />
          )}
        </div>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm leading-tight line-clamp-2">{listing.title}</h3>
            {listing.condition && <Badge variant="secondary" className="shrink-0 text-xs">{conditionLabel}</Badge>}
          </div>
          {listing.visibility === 'group_only' && (
            <Badge variant="outline" className="gap-1 border-orange-300 text-orange-700 bg-orange-50 text-xs" data-testid={`group-only-badge-${listing.listing_id}`}>
              <Users className="w-3 h-3" /> Nur Gruppe
            </Badge>
          )}
          {(listing.manufacturer_name || listing.model_name) && (
            <p className="text-xs text-muted-foreground">
              {listing.manufacturer_name}{listing.model_name ? ` · ${listing.model_name}` : ''}
              {listing.year_of_manufacture ? ` · ${listing.year_of_manufacture}` : ''}
            </p>
          )}
          <div className="flex items-center justify-between text-sm pt-1">
            <span className="font-semibold text-orange-600 flex items-center gap-1">
              <Euro className="w-3.5 h-3.5" />
              {listing.price_eur != null ? Number(listing.price_eur).toLocaleString('de-DE') : 'Auf Anfrage'}
            </span>
            {listing.country && (
              <span className="text-muted-foreground text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3" />{listing.country}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
